import { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useSearchParams } from "react-router-dom";
import { useCustomer } from "../../../contexts/CustomerContext";
import { useKronkOrders } from "../../../contexts/KronkOrdersContext";
import { useWeek } from "../../../contexts/WeekContext";
import { isWithinInterval, startOfDay, endOfDay } from "date-fns";

// Import shared components
import OrdersHeader from "../../../components/orders/OrdersHeader";
import OrdersFilters from "../../../components/orders/OrdersFilters";
import OrdersTable from "../../../components/orders/OrdersTable";
import OrdersCards from "../../../components/orders/OrdersCards";

// Utility functions for Shopify orders
const getShopifyOrderStatus = (order) => {
    if (order.cancelled_at) return 'Cancelled';
    if (order.closed_at) return 'Closed';
    if (order.fulfillment_status === 'fulfilled') return 'Fulfilled';
    if (order.fulfillment_status === 'partial') return 'Partially Fulfilled';
    if (order.financial_status === 'paid') return 'Paid';
    if (order.financial_status === 'pending') return 'Pending Payment';
    if (order.status === 'open') return 'Open';
    return order.status || 'Unknown';
};

const getShopifyOrderStatusColor = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === 'fulfilled' || statusLower === 'paid') {
        return 'text-green-500 tag-green';
    }
    if (statusLower === 'pending' || statusLower === 'open' || statusLower === 'partially fulfilled') {
        return 'text-yellow-600 tag-orange';
    }
    if (statusLower === 'cancelled' || statusLower === 'closed') {
        return 'text-error tag-red';
    }
    return 'text-font-secondary tag-secondary';
};

const getShopifyOrderStatusStyles = (status) => {
    const statusLower = status?.toLowerCase();
    
    if (statusLower === 'fulfilled') {
        return { 
            textColor: 'text-green-500', 
            icon: 'material-symbols:check-circle-outline',
            className: 'text-green-500 tag-green'
        };
    }

    if (statusLower === 'paid') {
        return { 
            textColor: 'text-green-500', 
            icon: 'tdesign:money-filled',
            className: 'text-green-500 tag-green'
        };
    }
    
    if (statusLower === 'pending' || statusLower === 'open' || statusLower === 'partially fulfilled') {
        return { 
            textColor: 'text-yellow-500', 
            icon: 'carbon:incomplete-error',
            className: 'text-yellow-600 tag-orange'
        };
    }
    
    if (statusLower === 'cancelled') {
        return { 
            textColor: 'text-error', 
            icon: 'material-symbols:cancel-outline',
            className: 'text-error tag-red'
        };
    }

    if (statusLower === 'closed') {
        return { 
            textColor: 'text-primary', 
            icon: 'mdi:package-delivered',
            className: 'text-primary tag-primary'
        };
    }
    
    return { 
        textColor: 'text-font-secondary', 
        icon: 'ph:question',
        className: 'text-font-secondary tag-secondary'
    };
};

const getShopifyLineItems = (order) => {
    if (order.line_items && Array.isArray(order.line_items) && order.line_items.length > 0) {
        return order.line_items
            .map(item => `${item.title}${item.quantity > 1 ? ` (x${item.quantity})` : ''}`)
            .join(', ');
    }
    return 'No items';
};

const getShopifyTotalAmount = (order) => {
    if (order.total_price) {
        return `$${parseFloat(order.total_price).toFixed(2)}`;
    }
    return 'N/A';
};

const formatShopifyDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch (e) {
        return dateString;
    }
};

// Helper function to check if an order is from chatbot
const isChatbotOrder = (order) => {
    if (!order || !order.utm_tracking) {
        return false;
    }
    
    const utmSource = order.utm_tracking?.utm_source?.toLowerCase()?.trim();
    const utmMedium = order.utm_tracking?.utm_medium?.toLowerCase()?.trim();
    
    return utmSource === 'chatbot' || utmMedium === 'chat_widget';
};

const Orders = () => {
    const { getEffectiveCustomerId } = useCustomer();
    const { orders, loading, error, refreshOrders } = useKronkOrders();
    const { dateRange } = useWeek();
    const [searchParams] = useSearchParams();
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [isRefreshing, setIsRefreshing] = useState(false);
    
    const [animationStates, setAnimationStates] = useState({
        header: false,
        filters: false,
        content: false
    });

    // Filter states
    const [activeFilters, setActiveFilters] = useState({
        orderTime: null,
        orderStatus: null,
        chatbot: null // null = all, true = chatbot only, false = non-chatbot only
    });

    // State for order status dropdown
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);

    // Track the currently active filter
    const [activeFilter, setActiveFilter] = useState(null);

    // Mobile filters state
    const [showMobileFilters, setShowMobileFilters] = useState(false);

    // Search state
    const [searchText, setSearchText] = useState('');

    const tableBodyRef = useRef(null);
    const rowRef = useRef(null);
    const cardRef = useRef(null);

    // Apply filters when they change
    useEffect(() => {
        let filtered = [...orders];

        // Apply date range filter
        if (dateRange?.start && dateRange?.end) {
            const rangeStart = startOfDay(dateRange.start);
            const rangeEnd = endOfDay(dateRange.end);
            
            filtered = filtered.filter(order => {
                if (!order.created_at) return false;
                try {
                    const orderDate = new Date(order.created_at);
                    return isWithinInterval(orderDate, { start: rangeStart, end: rangeEnd });
                } catch (e) {
                    return false;
                }
            });
        }

        // Apply search filter
        if (searchText.trim() !== '') {
            const searchLower = searchText.toLowerCase();
            filtered = filtered.filter(order => {
                // Search in order number
                const orderNumber = String(order.order_number || order.name || '').toLowerCase();
                // Search in customer name
                const customerName = (order.customer?.first_name || '').toLowerCase() + ' ' + 
                                   (order.customer?.last_name || '').toLowerCase();
                // Search in customer email
                const customerEmail = (order.customer?.email || order.email || '').toLowerCase();
                // Search in line items
                const lineItems = getShopifyLineItems(order).toLowerCase();
                // Search in order date
                const orderDate = formatShopifyDate(order.created_at).toLowerCase();
                
                return orderNumber.includes(searchLower) ||
                       customerName.includes(searchLower) ||
                       customerEmail.includes(searchLower) ||
                       lineItems.includes(searchLower) ||
                       orderDate.includes(searchLower);
            });
        }

        // Apply status filter
        if (activeFilters.orderStatus) {
            const statusFilter = activeFilters.orderStatus.toLowerCase();
            filtered = filtered.filter(order => {
                const orderStatus = getShopifyOrderStatus(order).toLowerCase();
                return orderStatus === statusFilter;
            });
        }

        // Apply chatbot filter
        if (activeFilters.chatbot !== null) {
            filtered = filtered.filter(order => {
                const isChatbot = isChatbotOrder(order);
                return activeFilters.chatbot === true ? isChatbot : !isChatbot;
            });
        }

        // Apply time sorting
        if (activeFilters.orderTime) {
            filtered.sort((a, b) => {
                const timeA = a.created_at || '';
                const timeB = b.created_at || '';
                return activeFilters.orderTime === 'asc' 
                    ? timeA.localeCompare(timeB) 
                    : timeB.localeCompare(timeA);
            });
        } else {
            // Default: sort by created_at descending (newest first)
            filtered.sort((a, b) => {
                const timeA = a.created_at || '';
                const timeB = b.created_at || '';
                return timeB.localeCompare(timeA);
            });
        }

        setFilteredOrders(filtered);
    }, [orders, activeFilters, searchText, dateRange]);


    // Animation effects
    useEffect(() => {
        const timer1 = setTimeout(() => setAnimationStates(prev => ({ ...prev, header: true })), 100);
        const timer2 = setTimeout(() => setAnimationStates(prev => ({ ...prev, filters: true })), 300);
        const timer3 = setTimeout(() => setAnimationStates(prev => ({ ...prev, content: true })), 500);

        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
            clearTimeout(timer3);
        };
    }, []);

    // Handle refresh
    const handleRefresh = async () => {
        if (isRefreshing) return;
        setIsRefreshing(true);
        await refreshOrders();
        setIsRefreshing(false);
    };

    // Get available status types
    const getAvailableStatusTypes = () => {
        const statuses = new Set();
        orders.forEach(order => {
            statuses.add(getShopifyOrderStatus(order));
        });
        return Array.from(statuses).sort();
    };

    // Handle status selection
    const handleStatusSelect = (status) => {
        const newFilters = {
            ...activeFilters,
            orderStatus: status
        };
        setActiveFilters(newFilters);
        setActiveFilter('orderStatus');
        setShowStatusDropdown(false);
    };

    // Handle filter clicks
    const handleFilterClick = (filterName) => {
        let newFilters = { ...activeFilters };
        let newActiveFilter = activeFilter;

        if (filterName === 'orderTime') {
            if (activeFilter === filterName) {
                newFilters[filterName] = activeFilters[filterName] === 'asc' ? 'desc' : 'asc';
            } else {
                newFilters = {
                    orderTime: 'desc',
                    orderStatus: null,
                    chatbot: null
                };
                newActiveFilter = filterName;
            }
        } else if (filterName === 'orderStatus') {
            setShowStatusDropdown(!showStatusDropdown);
            return;
        } else if (filterName === 'chatbot') {
            // Cycle through: null -> true -> false -> null
            if (activeFilter === filterName) {
                if (activeFilters.chatbot === null) {
                    newFilters.chatbot = true;
                } else if (activeFilters.chatbot === true) {
                    newFilters.chatbot = false;
                } else {
                    newFilters.chatbot = null;
                    newActiveFilter = null;
                }
            } else {
                newFilters = {
                    orderTime: null,
                    orderStatus: null,
                    chatbot: true
                };
                newActiveFilter = filterName;
            }
        }

        setActiveFilters(newFilters);
        setActiveFilter(newActiveFilter);
    };

    // Reset all filters
    const resetFilters = () => {
        setActiveFilters({
            orderTime: null,
            orderStatus: null,
            chatbot: null
        });
        setActiveFilter(null);
        setSearchText('');
    };

    // Check if any filter is active
    const isAnyFilterActive = activeFilter !== null || searchText.trim() !== '';

    // Wrapper functions for compatibility with shared components
    const getOrderStatus = (order) => {
        return getShopifyOrderStatus(order);
    };

    const getOrderStatusColor = (status) => {
        return getShopifyOrderStatusColor(status);
    };

    const getTotalAmount = (order) => {
        return getShopifyTotalAmount(order);
    };

    const getEquipmentList = (order) => {
        return getShopifyLineItems(order);
    };

    // Check if customer is Kronk
    const customerId = getEffectiveCustomerId();
    if (customerId !== 'kronk') {
        return (
            <div className="responsive-page-layout">
                <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                        <Icon icon="material-symbols:block" className="text-4xl text-bgAccent mb-4" />
                        <p className="text-font-secondary">Orders are only available for Kronk customer</p>
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="responsive-page-layout">
                <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                        <Icon icon="eos-icons:loading" className="text-4xl text-primary animate-spin mb-4" />
                        <p className="text-font-secondary">Loading orders...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="responsive-page-layout">
                <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                        <Icon icon="material-symbols:error-outline" className="text-4xl text-error mb-4" />
                        <p className="text-error">Error loading orders: {error}</p>
                        <button
                            onClick={() => refreshOrders()}
                            className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-70 transition-colors"
                        >
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="responsive-page-layout">
            {/* Header */}
            <OrdersHeader 
                animationStates={animationStates}
                viewMode="allLogs"
                handleViewModeToggle={() => {}}
                customerId={getEffectiveCustomerId()}
            />

            {/* Content */}
            <div className={`w-full flex-1 flex flex-col gap-3 min-h-0 transition-all duration-500 ease-out ${
                animationStates.content
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-4'
            }`}>
                {/* Filters */}
                <div className="w-full flex-shrink-0 transition-all duration-500 ease-out z-10">
                        <div className="w-full flex flex-row items-start sm:items-center justify-between gap-2">
                            {/* Desktop Filters */}
                            <div className="flex-row gap-2 items-center hidden xl:flex">
                                {/* Search Bar */}
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        placeholder="Search orders..."
                                        className="p-2 button-container rounded-md text-xs sm:text-sm w-full sm:w-auto pl-3 pr-10 min-w-[200px]"
                                    />
                                    {searchText && (
                                        <button
                                            onClick={() => setSearchText('')}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 text-accent hover:text-primary transition-colors"
                                        >
                                            <Icon icon="material-symbols:close" className="text-lg" />
                                        </button>
                                    )}
                                </div>

                                <button
                                    onClick={() => handleFilterClick('orderTime')}
                                    className={`flex flex-row items-center gap-2 justify-between p-2 ${activeFilter === 'orderTime' ? 'bg-primary-20 border-primary-30 border' : 'bg-bgSecondary md:button-container'} w-fit rounded-md text-sm`}
                                >
                                    <div className="flex flex-row items-center gap-1">
                                        <Icon icon="stash:data-date" className={`text-xl ${activeFilter === 'orderTime' ? 'text-primary' : 'text-accent'}`} />
                                        Order Time
                                    </div>
                                    {activeFilter === 'orderTime' && (
                                        <Icon
                                            icon={activeFilters.orderTime === 'asc' ? 'material-symbols:arrow-upward' : 'material-symbols:arrow-downward'}
                                            className="text-xl text-primary"
                                        />
                                    )}
                                </button>

                                {/* Order Status Filter */}
                                <div className="relative">
                                    <button
                                        onClick={() => handleFilterClick('orderStatus')}
                                        className={`flex flex-row items-center gap-2 justify-between p-2 ${activeFilter === 'orderStatus' ? 'bg-primary-20 border-primary-30 border' : 'bg-bgSecondary md:button-container'} w-fit rounded-md text-sm`}
                                    >
                                        <div className="flex flex-row items-center gap-1">
                                            <Icon icon="material-symbols:order-approve-outline" className={`text-xl ${activeFilter === 'orderStatus' ? 'text-primary' : 'text-accent'}`} />
                                            {activeFilter === 'orderStatus' && activeFilters.orderStatus ? activeFilters.orderStatus : 'Order Status'}
                                        </div>
                                        <Icon
                                            icon={showStatusDropdown ? 'material-symbols:keyboard-arrow-up' : 'material-symbols:keyboard-arrow-down'}
                                            className={`text-xl ${activeFilter === 'orderStatus' ? 'text-primary' : 'text-accent'}`}
                                        />
                                    </button>

                                    {showStatusDropdown && (
                                        <>
                                            <div className="absolute top-full left-0 mt-2 bg-bgSecondary card-container rounded-lg border border-bgAccent z-50 min-w-[200px]">
                                                <div className="flex flex-col">
                                                    {getAvailableStatusTypes().map(status => (
                                                        <button
                                                            key={status}
                                                            onClick={() => handleStatusSelect(status)}
                                                            className={`text-left p-2 rounded transition-colors text-sm ${
                                                                activeFilters.orderStatus === status ? 'bg-primary-20 border-primary-30 border' : 'bg-bgSecondary'
                                                            }`}
                                                        >
                                                            {status}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            <div
                                                className="fixed inset-0 z-40"
                                                onClick={() => setShowStatusDropdown(false)}
                                            />
                                        </>
                                    )}
                                </div>

                                {/* Chatbot Filter */}
                                <button
                                    onClick={() => handleFilterClick('chatbot')}
                                    className={`flex flex-row items-center gap-2 justify-between p-2 ${activeFilter === 'chatbot' ? 'bg-primary-20 border-primary-30 border' : 'bg-bgSecondary md:button-container'} w-fit rounded-md text-sm`}
                                >
                                    <div className="flex flex-row items-center gap-1">
                                        <Icon icon="material-symbols:smart-toy" className={`text-xl ${activeFilter === 'chatbot' ? 'text-primary' : 'text-accent'}`} />
                                        {activeFilter === 'chatbot' && activeFilters.chatbot === true 
                                            ? 'Chatbot Only' 
                                            : activeFilter === 'chatbot' && activeFilters.chatbot === false
                                            ? 'Non-Chatbot'
                                            : 'Chatbot'}
                                    </div>
                                    {activeFilter === 'chatbot' && (
                                        <Icon
                                            icon={activeFilters.chatbot === true ? 'material-symbols:check' : 'material-symbols:close'}
                                            className="text-xl text-primary"
                                        />
                                    )}
                                </button>

                                <div className="flex items-center gap-1 text-sm text-font-secondary whitespace-nowrap">
                                    <Icon icon="material-symbols:info-outline" className="text-lg" />
                                    <span><b className="text-primary">{filteredOrders.length}</b> orders found</span>
                                </div>

                                {isAnyFilterActive && (
                                    <button
                                        onClick={resetFilters}
                                        className="flex flex-row items-center gap-2 p-2 button-container whitespace-nowrap text-sm ml-auto rounded-md transition-colors"
                                    >
                                        <Icon icon="material-symbols:refresh" className="text-xl text-accent" />
                                        Reset Filters
                                    </button>
                                )}
                            </div>

                            {/* Mobile Filters */}
                            <div className="w-full xl:hidden flex flex-col gap-2">
                                {/* Mobile Search Bar */}
                                <div className="relative w-full">
                                    <input
                                        type="text"
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        placeholder="Search orders..."
                                        className="p-2 button-container rounded-md text-xs sm:text-sm w-full pl-3 pr-10"
                                    />
                                    {searchText && (
                                        <button
                                            onClick={() => setSearchText('')}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 text-accent hover:text-primary transition-colors"
                                        >
                                            <Icon icon="material-symbols:close" className="text-lg" />
                                        </button>
                                    )}
                                </div>
                                
                                <div className="w-fit relative">
                                    <button
                                        onClick={() => setShowMobileFilters(!showMobileFilters)}
                                        className="flex flex-row items-center gap-2 p-2 button-container text-sm rounded-md transition-colors w-full justify-center"
                                    >
                                        <Icon icon="material-symbols:filter-list" className="text-xl text-accent" />
                                        All Filters
                                        {isAnyFilterActive && (
                                            <span className="bg-primary text-white text-xs px-2 py-1 rounded-full ml-2">
                                                {Object.values(activeFilters).filter(Boolean).length}
                                            </span>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                {/* Orders List */}
                {filteredOrders.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full rounded-lg p-8">
                        <Icon icon="material-symbols:shopping-cart-off-outline" className="text-6xl text-bgAccent mb-4" />
                        <h3 className="text-xl text-font-primary mb-2">No Orders Found</h3>
                        <p className="text-font-secondary text-center">
                            No orders match your current filters. Try adjusting your filters or check back later.
                        </p>
                    </div>
                ) : (
                    <div className="flex flex-1 gap-2 h-full min-h-0">
                        {/* Orders Table */}
                        <div className="flex flex-col w-full transition-all duration-300 ease-out">
                            <div ref={tableBodyRef} className="w-full h-full flex flex-col overflow-hidden">
                                {/* Desktop Table - Custom for Shopify */}
                                <div className="flex-shrink-0 hidden md:block">
                                    <table className="w-full table-auto text-left border-collapse text-sm">
                                        <thead>
                                            <tr className="text-font-primary h-[48px] rounded-md card-container font-semibold">
                                                <th className="w-[200px] max-w-[200px] truncate pl-4 pr-2">Items</th>
                                                <th className="w-[120px] max-w-[120px] truncate pr-2">Order #</th>
                                                <th className="w-[120px] max-w-[120px] truncate pr-2">Date</th>
                                                <th className="w-[100px] max-w-[100px] truncate pr-2">Status</th>
                                                <th className="w-[100px] max-w-[100px] truncate pr-2">Total</th>
                                                <th className="w-[100px] max-w-[100px] truncate pr-4">Chatbot</th>
                                            </tr>
                                        </thead>
                                    </table>
                                </div>
                                
                                <div className="flex-1 overflow-y-auto scrollable hidden md:block">
                                    <table className="w-full table-auto text-left border-collapse text-sm">
                                        <tbody>
                                            {filteredOrders.map((order, index) => {
                                                const ref = index === 0 ? rowRef : null;
                                                return (
                                                    <tr
                                                        key={order.id}
                                                        ref={ref}
                                                        className="h-[56px] border-b border-border group text-font-secondary transition-all ease-out-in duration-300"
                                                    >
                                                        <td className="w-[200px] max-w-[200px] truncate font-semibold pl-4 pr-2 text-font-primary">
                                                            {getShopifyLineItems(order)}
                                                        </td>
                                                        <td className="w-[120px] max-w-[120px] truncate pr-2 text-font-primary">
                                                            #{order.order_number || order.name}
                                                        </td>
                                                        <td className="w-[120px] max-w-[120px] truncate pr-2 group-hover:text-primary">
                                                            {formatShopifyDate(order.created_at)}
                                                        </td>
                                                        <td className="w-[100px] max-w-[100px] truncate pr-2">
                                                            {(() => {
                                                                const status = getShopifyOrderStatus(order);
                                                                const { icon, className } = getShopifyOrderStatusStyles(status);
                                                                return (
                                                                    <div className={`w-fit font-semibold p-1.5 rounded text-xs flex items-center gap-0.5 text-font-primary justify-center ${className}`}>
                                                                        <Icon icon={icon} className="text-sm sm:text-base" />
                                                                        <span className="text-font-primary">{status}</span>
                                                                    </div>
                                                                );
                                                            })()}
                                                        </td>
                                                        <td className={`w-[100px] max-w-[100px] truncate pr-2 font-semibold text-primary`}>
                                                            {getShopifyTotalAmount(order)}
                                                        </td>
                                                        <td className="w-[100px] max-w-[100px] truncate pr-4">
                                                            {(() => {
                                                                const isChatbot = isChatbotOrder(order);
                                                                const icon = isChatbot ? 'material-symbols:check' : 'material-symbols:close';
                                                                const colorClass = isChatbot ? 'text-primary' : 'text-error';
                                                                return (
                                                                    <Icon icon={icon} className={`text-sm sm:text-xl ${colorClass}`} />
                                                                );
                                                            })()}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Mobile Card View */}
                                <div className="flex-1 overflow-y-auto md:hidden">
                                    <div className="xl:hidden space-y-2 p-2">
                                        {filteredOrders.map((order) => {
                                            return (
                                                <div
                                                    key={order.id}
                                                    className="p-3 flex flex-col gap-3 rounded-md transition-all duration-300 ease-out-in bg-background"
                                                >
                                                    <div className="text-base font-semibold flex items-center justify-between">
                                                        <span className="text-font-primary">
                                                            {getShopifyLineItems(order)}
                                                        </span>
                                                        <span className="text-sm text-font-secondary">
                                                            #{order.order_number || order.name}
                                                        </span>
                                                    </div>

                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            {(() => {
                                                                const status = getShopifyOrderStatus(order);
                                                                const { icon, className } = getShopifyOrderStatusStyles(status);
                                                                return (
                                                                    <div className={`p-1.5 rounded text-xs flex items-center gap-0.5 text-font-primary justify-center ${className}`}>
                                                                        <Icon icon={icon} className="text-lg" />
                                                                        <span className="text-font-primary">{status}</span>
                                                                    </div>
                                                                );
                                                            })()}
                                                            {(() => {
                                                                const isChatbot = isChatbotOrder(order);
                                                                const icon = isChatbot ? 'material-symbols:check' : 'material-symbols:close';
                                                                const colorClass = isChatbot ? 'text-primary' : 'text-error';
                                                                return (
                                                                    <Icon icon={icon} className={`text-lg ${colorClass}`} />
                                                                );
                                                            })()}
                                                        </div>
                                                        <span className="text-sm font-semibold text-primary">
                                                            {getShopifyTotalAmount(order)}
                                                        </span>
                                                    </div>

                                                    <div className="flex items-start justify-between">
                                                        <span className="text-sm text-font-secondary">
                                                            {formatShopifyDate(order.created_at)}
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Orders;

